import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, CalendarClock, XCircle, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { fetchWithAuth } from './IntelligenceLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_FILTERS = [
  { key: '', label: 'All' },
  { key: 'Scheduled', label: 'Scheduled' },
  { key: 'Sent', label: 'Sent' },
  { key: 'Needs Review', label: 'Needs Review' },
  { key: 'Failed', label: 'Failed' },
  { key: 'Cancelled', label: 'Cancelled' },
];

const STATUS_STYLES = {
  Scheduled: 'bg-blue-50 text-blue-700 border-blue-200',
  Sending: 'bg-amber-50 text-amber-700 border-amber-200',
  Sent: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Needs Review': 'bg-orange-50 text-orange-700 border-orange-200',
  Failed: 'bg-red-50 text-red-700 border-red-200',
  Cancelled: 'bg-gray-100 text-gray-500 border-gray-200',
};

const CRMScheduledSends = ({ onOpenCompany }) => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    fetchWithAuth(`${API_URL}/api/crm/scheduled-sends?${params.toString()}`)
      .then((res) => setItems(res.items || []))
      .catch(() => toast.error('Could not load scheduled sends'))
      .finally(() => setLoading(false));
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const cancel = async (id) => {
    setCancellingId(id);
    try {
      const token = localStorage.getItem('backoffice_token');
      const res = await fetch(`${API_URL}/api/crm/scheduled-sends/${id}/cancel`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Could not cancel');
      toast.success('Scheduled send cancelled');
      load();
    } catch (e) {
      toast.error(e.message || 'Could not cancel');
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="crm-scheduled-sends-panel">
      <div className="flex flex-wrap gap-2" data-testid="scheduled-sends-status-filters">
        {STATUS_FILTERS.map((f) => (
          <button key={f.key || 'all'} onClick={() => setStatus(f.key)} data-testid={`scheduled-filter-${f.key || 'all'}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              status === f.key ? 'bg-[#0A1F44] text-white border-[#0A1F44]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#0A1F44]/40'
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm" data-testid="scheduled-sends-empty">No scheduled sends in this view.</div>
      ) : (
        <div className="space-y-2" data-testid="scheduled-sends-list">
          {items.map((item) => (
            <div key={item.id} className="bg-white border border-gray-200 rounded-xl p-4" data-testid={`scheduled-send-${item.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => onOpenCompany && onOpenCompany(item.company_id)} data-testid={`scheduled-send-company-${item.id}`}
                      className="text-sm font-semibold text-[#0A1F44] hover:underline truncate">{item.company_name}</button>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${STATUS_STYLES[item.status] || 'bg-gray-100 text-gray-600 border-gray-200'}`} data-testid={`scheduled-send-status-${item.id}`}>
                      {item.status}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1.5 flex-wrap">
                    <CalendarClock className="h-3 w-3" /> {new Date(item.scheduled_at).toLocaleString()}
                    <span className="text-gray-300">•</span>
                    <Mail className="h-3 w-3" /> {item.recipient_email}
                    <span className="text-gray-300">•</span>
                    {item.contact_name}
                  </p>
                  <p className="text-xs text-gray-600 mt-1 truncate">{item.subject}</p>
                  {item.error && (
                    <p className="text-xs text-red-600 mt-1" data-testid={`scheduled-send-error-${item.id}`}>{item.error}</p>
                  )}
                  <button onClick={() => setExpandedId(expandedId === item.id ? null : item.id)} data-testid={`scheduled-send-toggle-body-${item.id}`}
                    className="text-[11px] text-gray-400 underline mt-1">{expandedId === item.id ? 'Hide message' : 'View message'}</button>
                  {expandedId === item.id && (
                    <pre className="text-xs text-gray-600 bg-gray-50 rounded-lg p-2 mt-1 whitespace-pre-wrap">{item.body}</pre>
                  )}
                </div>
                {item.status === 'Scheduled' && (
                  <button onClick={() => cancel(item.id)} disabled={cancellingId === item.id} data-testid={`scheduled-send-cancel-${item.id}`}
                    className="flex items-center gap-1 text-xs font-medium text-red-600 hover:underline disabled:opacity-50 whitespace-nowrap">
                    {cancellingId === item.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />} Cancel
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CRMScheduledSends;
